import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { m } from "framer-motion";
import { useTranslation } from "react-i18next";
import { client, writeClient } from "../../lib/sanity";
import { PortableText } from "@portabletext/react";
import { urlForImage, urlForImageWithVanity } from "../../lib/sanityImageUrl";
import PostLayout from "../blog/PostLayout";
import PortableTextComponents from "../blog/SanityComponents/PortableTextComponents";
import { BookSVG } from "../../Components/BookSVG";
import { GoShare } from "react-icons/go";
import { FaLink, FaFacebook, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { extractHeadings } from "../../utils/tableOfContents";
import { createSlug } from "../../utils/tableOfContents";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// This function should match the slug generation in tableOfContents.js
const createHeadingSlug = (text) => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics (accents)
    .replace(/[^\w\s-]/g, "") // Remove special characters except hyphens
    .trim()
    .replace(/\s+/g, "-"); // Replace spaces with hyphens
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const calculateReadTime = (body, markdownBody) => {
  const wordsPerMinute = 200;
  if (markdownBody) {
    const plainText = markdownBody.replace(/[#*_\[\]()>|`~-]/g, "");
    const totalWords = plainText.split(/\s+/).filter(Boolean).length;
    return Math.ceil(totalWords / wordsPerMinute);
  }
  if (!body) return 0;
  let totalWords = 0;
  body.forEach((block) => {
    if (block._type === "block") {
      const text = block.children
        .filter((child) => child._type === "span")
        .map((span) => span.text)
        .join(" ");
      totalWords += text.split(/\s+/).length;
    }
  });
  return Math.ceil(totalWords / wordsPerMinute);
};

const ExplainLayout = () => {
  const { t } = useTranslation(['common']);
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  // Copy to clipboard function
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, []);

  // Extended components with newsletter handler
  const extendedComponents = useMemo(() => {
    return {
      ...PortableTextComponents,
      types: {
        ...PortableTextComponents.types,
        tableOfContents: ({ value }) => {
          console.log("Rendering tableOfContents with value:", value);

          try {
            // Extract headings from the post body
            const headings = extractHeadings(post?.body || []);
            console.log("Extracted headings:", headings);

            if (headings.length === 0) {
              return (
                <div className='table-of-contents-container'>
                  <h2 className='table-of-contents-title'>
                    {value?.title || "V článku sa dozviete"}
                  </h2>
                  <p className='table-of-contents-empty'>
                    No headings found in this article.
                  </p>
                </div>
              );
            }

            return (
              <div className='table-of-contents-container'>
                <h2 className='table-of-contents-title'>
                  {value?.title || "V článku sa dozviete"}
                </h2>
                <nav className='table-of-contents-nav'>
                  <ul>
                    {headings.map((heading, index) => (
                      <li key={index} className={`toc-level-${heading.level}`}>
                        <a
                          href={`#${heading.slug}`}
                          className='toc-link'
                          onClick={(e) => {
                            e.preventDefault();
                            const element = document.getElementById(
                              heading.slug
                            );
                            if (element) {
                              element.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            } else {
                              console.warn(
                                `Element with ID #${heading.slug} not found`
                              );
                            }
                          }}
                        >
                          {heading.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            );
          } catch (error) {
            console.error("Error rendering Table of Contents:", error);
            return (
              <div className='table-of-contents-error'>
                <h2 className='table-of-contents-error-title'>
                  Table of Contents Error
                </h2>
                <p className='table-of-contents-error-message'>
                  {error.message ||
                    "An error occurred rendering the table of contents"}
                </p>
              </div>
            );
          }
        },
      },
      context: {
        blocks: post?.body || [],
      },
    };
  }, [post?.body]);

  useEffect(() => {
    if (post?.body) {
      console.log(
        "Found post body with types:",
        post.body
          .map((block) => block._type)
          .filter((type, i, arr) => arr.indexOf(type) === i)
      );

      // Find tableOfContents blocks
      const tocBlocks = post.body.filter(
        (block) => block._type === "tableOfContents"
      );
      console.log("TableOfContents blocks:", tocBlocks);
    }
  }, [post?.body]);

  useEffect(() => {
    if (post?.body) {
      // Find h2, h3, h4 blocks
      const headingBlocks = post.body.filter(
        (block) =>
          block._type === "block" && ["h2", "h3", "h4"].includes(block.style)
      );

      console.log("Heading blocks found:", headingBlocks);

      // Test the extractHeadings function
      const extractedHeadings = extractHeadings(post.body);
      console.log("Extracted headings:", extractedHeadings);
    }
  }, [post?.body]);

  useEffect(() => {
    if (post?.body) {
      setTimeout(() => {
        console.log("Checking TOC and heading IDs:");

        // Log all heading elements with their IDs
        const headings = document.querySelectorAll("h2, h3, h4");
        console.log(`Found ${headings.length} heading elements in DOM`);

        headings.forEach((heading) => {
          console.log(
            `Heading: "${heading.textContent.trim()}" has ID: "${heading.id}"`
          );
        });

        // Log all TOC links
        const tocLinks = document.querySelectorAll(".toc-link");
        console.log(`Found ${tocLinks.length} TOC links`);

        tocLinks.forEach((link) => {
          const href = link.getAttribute("href");
          console.log(
            `TOC link: "${link.textContent.trim()}" points to: "${href}"`
          );

          // Check if target exists
          const targetId = href.substring(1); // Remove the # character
          const targetExists = !!document.getElementById(targetId);
          console.log(`  Target element exists: ${targetExists}`);

          if (!targetExists) {
            // Find closest match to help debugging
            const allIds = Array.from(document.querySelectorAll("[id]")).map(
              (el) => el.id
            );
            console.log(`  All available IDs: ${allIds.join(", ")}`);
          }
        });
      }, 500); // Wait for content to be fully rendered
    }
  }, [post?.body]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Query including SEO fields and proper image fields
        const query = `*[_type == "explain" && slug.current == $slug][0]{
        _id,
        title,
        slug,
        excerpt,
        markdownBody,
        mainImage {
          asset->{
            _id,
            url,
            originalFilename,
            title,
            description,
            altText,
            metadata {
              dimensions {
                width,
                height
              }
            }
          },
          caption,
          alt
        },
          body[]{
          ...,
          _type == "block" => {
            ...,
            markDefs[]{
              ...,
              _type == "link" => {
                "href": href
              },
              _type == "tooltip" => {
                "text": text
              }
            }
          },
          _type == "tableOfContents" => {
            _type,
            title,
            includeHeadings
          },
          _type == "image" => {
            ...,
            asset->{
              _id,
              url,
              originalFilename,
              title,     
              description,  
              altText, 
              metadata {
                dimensions {
                  width,
                  height
                }
              }
            },
            caption,
            alt
          },
          _type == "customTweet" => {
            ...,
            author {
              ...,
              avatar {
                asset->{
                  _id,
                  url,
                  originalFilename,
                  metadata {
                    dimensions {
                      width,
                      height
                    }
                  }
                }
              }
            }
          },
          _type == "pollReference" => {
            ...,
            "poll": *[_type == "pollContent" && _id == ^._ref][0]{
              question,
              options,
              caption,
              _id
            }
          },
          _type == "faqSectionBlock" => {
            _type,
            faqData{
              heading,
              faqItems[]{
                question,
                answer
              }
            }
          },
          markDefs[]{
            ...,
            _type == "internalLink" => {
              "slug": @.reference->slug
            },
            _type == "tooltip" => {
              "text": text
            }
          }
        },
        publishedAt,
        categories[]->{
          title
        },
        author->{
          name,
          image {
            asset->{
              _id,
              url,
              originalFilename,
              title,
              description,
              altText,
              metadata {
                dimensions {
                  width,
                  height
                }
              }
            },
            caption,
            alt
          }
        },
        seo{
          metaTitle,
          metaDescription,
          seoKeywords,
          nofollowAttributes,
          openGraph{
            title,
            description,
            siteName,
            url,
            image{
              asset->{
                _id,
                url,
                originalFilename,
                metadata {
                  dimensions {
                    width,
                    height
                  }
                }
              },
              caption,
              alt
            }
          },
          twitter{
            handle,
            site,
            creator,
            cardType
          },
          additionalMetaTags[]{
            metaAttributes[]{
              attributeKey,
              attributeType,
              attributeValueString,
              attributeValueImage{
                asset->{
                  _id,
                  url,
                  originalFilename,
                  metadata {
                    dimensions {
                      width,
                      height
                    }
                  }
                },
                caption,
                alt
              }
            }
          }
        }
      }`;
        const fetchedPost = await client.fetch(query, { slug });
        console.log("Fetched post markdownBody:", fetchedPost?.markdownBody ? `${fetchedPost.markdownBody.substring(0, 100)}...` : "EMPTY/NULL");
        console.log("Fetched post body:", fetchedPost?.body ? `${fetchedPost.body.length} blocks` : "EMPTY/NULL");
        if (fetchedPost) {
          setPost(fetchedPost);
        } else {
          setError("Post not found in Sanity dataset");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(`Error fetching post: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Prepare SEO data regardless of loading/error state
  const postTitle = post?.title || "INAQ Blog";
  const postExcerpt = post?.excerpt || "";
  const postImage = post?.mainImage
    ? urlForImageWithVanity(post.mainImage)
    : "";
  const postSeoData = post?.seo || null;
  const postSlug = post
    ? `/vysvetlujeme/${post.slug.current}`
    : `/vysvetlujeme/${slug || ""}`;
  const categoryText =
    post?.categories && post.categories.length > 0
      ? ` | ${post.categories[0].title}`
      : "";
  const defaultTitle = post ? `${postTitle}${categoryText}` : "INAQ Blog";
  const structuredData = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        image: post.mainImage
          ? [urlForImageWithVanity(post.mainImage)]
          : ["https://www.inaq.sk/assets/img/webp/inaq_blog_logo_web.webp"],
        datePublished: post.publishedAt,
        dateModified: post._updatedAt || post.publishedAt,
        author: {
          "@type": "Person",
          name: post.author?.name || "INAQ",
        },
        publisher: {
          "@type": "Organization",
          name: "INAQ",
          logo: {
            "@type": "ImageObject",
            url: "https://www.inaq.sk/assets/img/webp/inaq_logo_web.webp",
          },
        },
        keywords:
          (post.seo?.seoKeywords?.length > 0
            ? post.seo.seoKeywords.join(", ")
            : post.categories?.map((cat) => cat.title).join(", ")) ||
          "B2B, marketing",
        description: post.excerpt,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://www.inaq.sk/blog/${post.slug?.current}`,
        },
      }
    : null;

  // Check if we should render client-side structured data
  // Don't render client-side if already rendered server-side
  const shouldRenderClientStructuredData = () => {
    if (typeof window === "undefined") return false; // We're on the server
    if (window.__PRERENDERED) return false; // Page was prerendered
    if (document.querySelector('script[type="application/ld+json"]'))
      return false; // Already has structured data
    return post && structuredData; // Only render if we have a post and structured data
  };

  return (
    <>
      <PostLayout
        seo={postSeoData}
        slug={postSlug}
        defaultTitle={defaultTitle}
        defaultDescription={postExcerpt}
        defaultOgImage={postImage}
        hideNewsletter={true}
        structuredData={structuredData}
      >
        {loading ? (
          <div className='max-w-[1280px] mx-auto px-[15px]'>
            <div>Loading post... Please wait.</div>
          </div>
        ) : error ? (
          <div className='max-w-[1280px] mx-auto px-[15px]'>
            <div>Error: {error}</div>
            <div>Attempted to load slug: {slug}</div>
          </div>
        ) : !post ? (
          <div className='max-w-[1280px] mx-auto px-[15px]'>
            <div>Post not found</div>
            <div>Attempted to load slug: {slug}</div>
          </div>
        ) : (
          <div className='max-w-[680px] mt-12 md:mt-8 pt-[80px] mx-auto'>
            <div className='text-md sm:mb-0 md:mb-2 text-gray-500 xxxl:mx-10 xxs:mx-4 block'>
              <Link to='/' className='hover:text-gray-700'>
                Vysvetľujeme
              </Link>
              {post.categories && post.categories.length > 0 && (
                <>
                  <span className='mx-2'>/</span>
                  <span className='text-gray-700'>
                    {post.categories[0].title}
                  </span>
                </>
              )}
              <span className='mx-2'>/</span>
              <span className='text-gray-900 font-medium cursor-default'>
                {post.title}
              </span>
            </div>
            <m.div
              className='visible overflow-hidden rounded-[6px] text-center p-0 xxxl:mx-10 xxs:mx-4'
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className='blog-title text-left font-[Oxanium] font-bold text-4xl text-darkgray mb-2 mt-3 sm:mt-2 md:mt-4 relative'>
                {post.title}
              </h1>
              <div className='border-none py-1'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center space-x-6'>
                    <div className='flex items-center space-x-2 mx-0 text-gray-500 text-sm'>
                      <span className='mr-2 cursor-default'>
                        {formatDate(post.publishedAt)}
                      </span>
                      <BookSVG className='w-4 h-4 mobs:hidden' />
                      <span className='cursor-default mobs:hidden'>
                        {calculateReadTime(post.body, post.markdownBody)} {t('common:blog.readTime', 'min čítania')}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center space-x-4'>
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: post.title,
                            url: window.location.href,
                          });
                        }
                      }}
                      className='text-gray-500 hover:text-gray-700'
                    >
                      <GoShare className='w-5 h-5' />
                    </button>
                  </div>
                </div>
              </div>
              <div className='blog-image'>
                {post.mainImage && (
                  <figure>
                    <img
                      className='w-full'
                      src={urlForImageWithVanity(post.mainImage)}
                      alt={
                        post.mainImage.asset?.altText ||
                        post.mainImage.asset?.title ||
                        post.mainImage.alt ||
                        post.title
                      }
                      title={
                        post.mainImage.asset?.title ||
                        post.mainImage.asset?.description ||
                        post.mainImage.caption ||
                        post.mainImage.asset?.altText ||
                        post.mainImage.alt ||
                        post.title
                      }
                    />
                    {post.mainImage.caption && (
                      <figcaption className='text-center font-rubik text-md text-gray-500 mt-3 main-image-caption'>
                        {post.mainImage.caption}
                      </figcaption>
                    )}
                  </figure>
                )}
              </div>
              <div className='blog-text inline-block w-full'>
                <div className='relative mx-auto mt-12 text-[17px] text-gray-900 font-rubik lg:w-full'>
                  <div className='text-left'>
                    {post.markdownBody ? (
                      <div className="markdown-content">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h2: ({children}) => <h2 id={createHeadingSlug(String(children))}>{children}</h2>,
                            h3: ({children}) => <h3 id={createHeadingSlug(String(children))}>{children}</h3>,
                            h4: ({children}) => <h4 id={createHeadingSlug(String(children))}>{children}</h4>,
                            table: ({children}) => (
                              <div className="table-scroll-wrapper" tabIndex="0" role="region">
                                <table className="markdown-table">{children}</table>
                              </div>
                            ),
                          }}
                        >
                          {post.markdownBody}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <PortableText
                        value={post.body}
                        components={extendedComponents}
                        context={{
                          blocks: post.body,
                        }}
                      />
                    )}
                    {/* Related Keywords Section */}
                    {post.seo?.seoKeywords &&
                      post.seo.seoKeywords.length > 0 && (
                        <div className='mt-6 pt-6 border-gray-300'>
                          <div className='flex flex-wrap items-start gap-4'>
                            <p className='text-gray-500 text-sm cursor-default'>
                              Súvisiace kľúčové slová:
                            </p>
                            <div className='flex flex-wrap gap-2'>
                              {post.seo.seoKeywords.map((keyword, index) => (
                                <div
                                  key={index}
                                  className='bg-inaqgold text-white text-xs px-3 py-1 cursor-default'
                                >
                                  <span>{keyword}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    {/* Share Section */}
                    <div className='share-section mt-10 pt-6 border-t border-gray-300'>
                      <div className='flex justify-between items-start flex-wrap'>
                        <div className='mb-4 md:mb-0 xs:hidden'>
                          {post.categories && post.categories.length > 0 && (
                            <div className='flex items-center bg-inaqgold text-white text-xs px-3 py-1 rounded-sm cursor-default'>
                              <span>{post.categories[0].title}</span>
                            </div>
                          )}
                        </div>

                        <div className='flex items-center gap-4 xs:mx-auto'>
                          <p className='text-gray-500 text-sm cursor-default'>
                            Zdieľajte vysvetlivku
                          </p>

                          <div className='relative'>
                            <button
                              onClick={copyToClipboard}
                              className='text-gray-500 hover:text-inaqgold transition-colors'
                              aria-label='Copy link'
                            >
                              <FaLink size={15} />
                            </button>
                            {linkCopied && (
                              <div className='absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-inaqgold text-white text-xs rounded-sm whitespace-nowrap'>
                                Odkaz skopírovaný
                              </div>
                            )}
                          </div>

                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-gray-500 hover:text-inaqgold transition-colors'
                            aria-label='Zdielať na Facebook'
                          >
                            <FaFacebook size={16} />
                          </a>

                          <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-gray-500 hover:text-inaqgold transition-colors'
                            aria-label='Zdielať na LinkedIn'
                          >
                            <FaLinkedin size={16} />
                          </a>

                          <a
                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-gray-500 hover:text-inaqgold transition-colors'
                            aria-label='Zdielať na X'
                          >
                            <FaXTwitter size={16} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </m.div>
          </div>
        )}
      </PostLayout>
    </>
  );
};

export default ExplainLayout;
